import cloneDeep from "lodash/cloneDeep";
import React, { ReactNode } from "react";

import { STATUS_CODE_CANCEL } from "cortex/ui/src/constant/app";

type ResponseMessage = string | string[] | Record<string, unknown>;
type ResponseData = {
  message?: ResponseMessage;
  [key: string]: unknown;
};
export type ResponseLike = {
  data?: ResponseData;
  status?: number;
};
type FormErrorState = {
  errors: boolean;
  formErrors: Record<string, unknown>;
  systemErrors: Record<string, unknown>;
};

const _formErrorsInitialState: FormErrorState = {
  errors: false,
  formErrors: {},
  systemErrors: {},
};

export function getErrorMessage(
  resp?: Error | ResponseLike | null,
  msg?: string,
): string {
  if (!resp) {
    return "";
  }

  if (resp instanceof Error) {
    return `${resp.name}: ${resp.message}`;
  }

  const { status } = resp;

  if (status === 200) {
    return "";
  }

  if (status === STATUS_CODE_CANCEL) {
    return "Request cancelled.";
  }

  const message = resp.data?.message ?? msg;

  if (typeof message === "string") {
    return message;
  }

  if (Array.isArray(message)) {
    return "<li>" + message.join("</li><li>") + "</li>";
  }

  if (message && typeof message === "object") {
    const lines = Object.entries(message).map(([k, v]) => `${k}: ${v}`);

    if (lines.length > 1) {
      return "<li>" + lines.join("</li><li>") + "</li>";
    }

    if (lines.length) {
      return lines[0];
    }
  }

  const statusMsg = status ? ` (${status})` : "";

  return `Failed to complete the request. Please try again.${statusMsg}`;
}

/*
 * This function allows us to easily map api responses to
 * ui component form validation. You can use the return value to directly
 * set component state and update form validation status accordingly.
 */
export function getFormErrors(resp: ResponseLike = {}): FormErrorState {
  const { status, data = {} } = resp;
  const errorState = cloneDeep(_formErrorsInitialState);

  if (!status) {
    return errorState;
  }

  if (status < 400) {
    return errorState;
  }

  errorState.errors = true;
  let { message } = data as { message?: ResponseMessage };
  // Make sure form/system errors is always an object.
  if (typeof message === "string") {
    message = { message };
  }

  if (status >= 500) {
    errorState.systemErrors = (message || {}) as Record<string, unknown>;
  } else {
    errorState.formErrors = (message || {}) as Record<string, unknown>;
  }

  return errorState;
}

export function getErrorDetails(
  resp?: ResponseLike | null,
  key = "extra",
): ReactNode {
  if (!resp || resp.status === 200) {
    return "";
  }

  if (resp.status === STATUS_CODE_CANCEL) {
    return "Request cancelled.";
  }

  let elemExtra: ReactNode = null;
  try {
    const extra = resp.data?.[key];

    if (!extra) {
      return null;
    }

    switch (typeof extra) {
      case "string":
        elemExtra = <li>{extra}</li>;
        break;
      case "object":
        elemExtra = [];
        if (Array.isArray(extra)) {
          elemExtra = extra.map((line, idx) => {
            return <li key={`err-detail-${idx}`}>{line}</li>;
          });
        } else {
          const items: ReactNode[] = [];
          for (const [k, v] of Object.entries(extra)) {
            items.push(<li key={k}>{`${k}: ${v}`}</li>);
          }
          elemExtra = items;
        }
        break;
      default:
        throw new TypeError(typeof extra);
    }
  } catch (e) {
    console.error("Failed to parse error details.", e);
  }

  return elemExtra ? <ul>{elemExtra}</ul> : elemExtra;
}
